import { uniq } from 'lodash';
import { put, takeLatest } from 'redux-saga/effects';
import { ProjectId, UserId } from 'api/models';
import { ProjectAPI } from 'api/project';
import { toastService } from 'components/molecules/toast';
import { addUsers } from 'data/members/slice';
import { createAndInviteProjectMembersAction } from 'data/projects/actions';
import { getProjectMembersQueryKey, queryClient } from 'queries';
import { getPeopleListQueryKey } from 'queries/customer-company';
import { MEMBERS_V2_MODIFIED_QUERY_KEY, MEMBERS_V2_QUERY_KEY } from 'queries/members';
import { add } from 'stores/memberships';
import { addedAll } from 'stores/users';
function* createAndInviteProjectMembers(action) {
    const { payload } = action;
    const { data, projectId, handleSuccess, handleFailure } = payload;
    try {
        const newUsers = data.filter(({ user })=>user.emailId != null);
        const existingUsers = data.filter(({ user })=>user.userId != null);
        const createdAndInvitedResponse = newUsers.length > 0 ? yield* function* createAndInviteNew() {
            try {
                return yield ProjectAPI.createAndInviteProjectMembers(projectId, newUsers);
            } catch (error) {
                var _error_response_data_errors, _error_response_data, _error_response;
                const errorData = error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : (_error_response_data = _error_response.data) === null || _error_response_data === void 0 ? void 0 : (_error_response_data_errors = _error_response_data.errors) === null || _error_response_data_errors === void 0 ? void 0 : _error_response_data_errors[0];
                const errorCode = errorData === null || errorData === void 0 ? void 0 : errorData.code;
                const errorReason = errorData === null || errorData === void 0 ? void 0 : errorData.reason;
                // Handle specific error codes with better messages
                // Backend error reason takes precedence, fallback to default messages
                let errorTitle = 'Failed to add team members';
                let errorMessage = errorReason || 'Could not add team members to your project';
                switch(errorCode){
                    case 'INVALID_INPUTS':
                        errorTitle = 'Invalid Input';
                        errorMessage = errorReason || 'One or more email addresses are invalid or already exist in the account.';
                        break;
                    case 'DUPLICATE_USER':
                        errorTitle = 'Already a Member';
                        errorMessage = errorReason || 'One or more users are already members of this project.';
                        break;
                    case 'PERMISSION_DENIED':
                        errorTitle = 'Permission Denied';
                        errorMessage = errorReason || 'You do not have permission to invite these users.';
                        break;
                    case 'COMPANY_CONFLICT':
                        errorTitle = 'User Conflict';
                        errorMessage = errorReason || 'One or more users already exist in a different company.';
                        break;
                    default:
                        // Use backend reason as-is for unknown codes
                        errorMessage = errorReason || errorMessage;
                }
                toastService({
                    title: errorTitle,
                    message: errorMessage
                }).error();
                // Pass error to handleFailure for additional handling
                if (handleFailure) {
                    handleFailure(error);
                }
                return [];
            }
        }() : [];
        const invitedResponse = existingUsers.length > 0 ? yield ProjectAPI.addMembers(projectId, existingUsers) : [];
        if (handleSuccess) {
            handleSuccess(payload);
        }
        yield put(addUsers(createdAndInvitedResponse.map(({ user })=>user)));
        yield put(addedAll(createdAndInvitedResponse.map(({ user })=>user)));
        yield put(add({
            projectId: ProjectId(projectId),
            memberships: createdAndInvitedResponse.concat(invitedResponse).map(({ user: { userId }, permissionSet: { permissionSetType: memberType }, joinedAt })=>({
                    userId: UserId(userId),
                    memberType,
                    joinedAt
                }))
        }));
        yield queryClient.refetchQueries(getProjectMembersQueryKey(ProjectId(projectId)));
        // Invite-time custom field values are written to the same account-level
        // Customer Portfolio People List record; refetch it per invited company
        // so those values (set on `newUsers[].user.fields`) show up there too.
        const invitedCompanyIds = uniq(newUsers.map(({ user })=>{
            var _user_company;
            return (_user_company = user.company) === null || _user_company === void 0 ? void 0 : _user_company.companyId;
        }).filter((companyId)=>companyId != null));
        for (const companyId of invitedCompanyIds){
            yield queryClient.refetchQueries(getPeopleListQueryKey(companyId));
        }
        yield queryClient.refetchQueries(MEMBERS_V2_QUERY_KEY);
        yield queryClient.refetchQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
        // Refetch budget exclusions queries for the project
        yield queryClient.refetchQueries([
            'project-members-exclusion',
            ProjectId(projectId)
        ]);
        yield queryClient.refetchQueries([
            'budget-member-exclusion'
        ]);
    } catch (error) {
        if (handleFailure) {
            handleFailure(error);
        }
    }
}
export const watchCreateAndInviteProjectMembers = function*() {
    yield takeLatest(createAndInviteProjectMembersAction, createAndInviteProjectMembers);
};
